import React, { useCallback, useMemo, useState } from "react";
import styles from "./CustomFieldSavedFilters.module.css";
import { ICustomFieldSearchForm } from "../CustomFieldFilterBuilder/CustomFieldFilterBuilder";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { CheckboxSquare } from "../../../common/formfields/CheckboxFormField/CheckboxFormField";
import GridLayout from "react-grid-layout";
import { FloatingDiv } from "../../../common/buttons/FloatingDiv/FloatingDiv";
import { RenderCustomFieldFilterType } from "../CustomFieldFilterBuilder/CustomFieldFilterRenderUtils";

interface CustomFieldSavedFiltersProps {
  savedFilters: ICustomFieldSearchForm[] | undefined;
  onReplace: (fields: ICustomFieldSearchForm[]) => void;
  onUpdate: (filter: ICustomFieldSearchForm) => void;
  onEditCallback: (filter: ICustomFieldSearchForm) => void;
  onDeleteCallback: (id: string) => void;
}
export const CustomFieldSavedFilters = ({
  savedFilters,
  onReplace,
  onUpdate,
  onEditCallback,
  onDeleteCallback,
}: CustomFieldSavedFiltersProps) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const layout = useMemo(
    () =>
      savedFilters?.map((col, index) => ({
        i: col.id,
        x: 0,
        y: index,
        w: 1,
        h: 1,
        isDraggable: true,
        isResizable: false,
      })) as GridLayout.Layout[],
    [savedFilters]
  );

  const onChangeHandle = useCallback(
    (filter: ICustomFieldSearchForm) => {
      onUpdate({ ...filter, showInSidebar: !filter.showInSidebar });
    },
    [onUpdate]
  );

  const onLayoutChange = useCallback(
    (layout: GridLayout.Layout[]) => {
      const sortedDict: Record<string, ICustomFieldSearchForm> = {};
      let result = [...(savedFilters ?? [])] as ICustomFieldSearchForm[];
      for (const l of layout) {
        const index = result?.findIndex((f) => f.id === l.i);
        if (index !== -1 && Array.isArray(savedFilters)) {
          // console.log(`Updated ${savedFilters[index].name} from index ${index} to ${l.y}`);
          sortedDict[l.y] = { ...savedFilters[index] };
        }
      }
      onReplace(Object.values(sortedDict));
    },
    [onReplace, savedFilters]
  );

  return (
    <>
      {savedFilters && !!savedFilters.length && (
        <FloatingDiv
          placement="bottom-end"
          triggerComponent={({ ref, getReferenceProps, isOpen, setOpen }) => (
            <button
              className={`btn btn-xs btn-default`}
              title={"Manage custom filters"}
              ref={ref}
              {...getReferenceProps()}
              style={{ marginLeft: "auto" }}
              disabled={!savedFilters || savedFilters.length === 0}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen((prev) => !prev);
              }}
              aria-label="Manage custom filters"
            >
              <LucideIcon name="list-checks" color="var(--primary)" />
            </button>
          )}
        >
          {({ open, setOpen }) => (
            <div
              className={styles.savedFiltersContainer}
              style={{ minHeight: layout ? layout.length * 26 : 0 }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <div className={styles.columns}>
                <div className={styles.column_head}>Custom filters</div>
                <div className={styles.columns_body}>
                  {layout && (
                    <GridLayout
                      layout={layout}
                      cols={1}
                      rowHeight={26}
                      preventCollision={false}
                      width={400}
                      // onLayoutChange={(layout) => onLayoutChange(layout)}
                      style={{ margin: "0px 0px", overflow: "auto" }}
                      margin={[0, 0]}
                      isBounded={true}
                      onDrag={() => setIsDragging(true)}
                      onDragStop={(layout: GridLayout.Layout[]) => {
                        setTimeout(() => setIsDragging(false), 100);
                        onLayoutChange(layout);
                      }}
                      draggableHandle=".DragHandle"
                    >
                      {savedFilters?.map((filter) => (
                        <div key={filter.id}>
                          <CustomFieldSavedFilter
                            id={filter.id}
                            filter={filter}
                            isDragging={isDragging}
                            onChangeHandle={onChangeHandle}
                            onEditCallback={onEditCallback}
                            onDeleteCallback={onDeleteCallback}
                            disabled={false}
                          />
                        </div>
                      ))}
                    </GridLayout>
                  )}
                </div>
              </div>
            </div>
          )}
        </FloatingDiv>
      )}
    </>

    // <Modal isOpen={showSavedFilters} onClose={() => setShowSavedFilters((prev) => false)}>
    //   <Modal.Title>Saved Filters</Modal.Title>
    //   <Modal.Body>

    //   </Modal.Body>
    //   <Modal.Controls>
    //     <></>
    //   </Modal.Controls>
    // </Modal>
  );
};

interface CustomFieldSavedFilterProps {
  id: string;
  filter: ICustomFieldSearchForm;
  isDragging: boolean;
  onChangeHandle: (filter: ICustomFieldSearchForm) => void;
  onEditCallback: (filter: ICustomFieldSearchForm) => void;
  onDeleteCallback: (id: string) => void;
  disabled?: boolean;
}
const CustomFieldSavedFilter = ({
  id,
  filter,
  isDragging,
  onChangeHandle,
  onEditCallback,
  onDeleteCallback,
  disabled,
}: CustomFieldSavedFilterProps) => {
  return (
    <div className={`${styles.column} DragHandle`} key={id.toString()}>
      <div className={"flex row-nowrap align-center"}>
        <strong>
          <LucideIcon name="grip-vertical" color={"var(--gray-400)"} />
        </strong>
      </div>
      <div
        className="flex row-nowrap align-center gap-5"
        onClick={(e) => {
          if (!isDragging) {
            e.preventDefault();
            e.stopPropagation();
            if (!disabled) onChangeHandle(filter);
          }
        }}
        style={{ cursor: "pointer", width: "100%", overflow: "hidden" }}
      >
        <CheckboxSquare
          checked={filter.showInSidebar}
          style={{ pointerEvents: "none", flexShrink: 0 }}
          disabled={disabled}
        />

        <div className={`${styles.label} ellipsisContainer`} style={{ pointerEvents: "none" }}>
          <span
            style={{
              fontStyle: "normal",
              fontWeight: 600,
              fontSize: "12px",
              lineHeight: "17px",
              textTransform: "uppercase",
            }}
          >
            {filter.name}
          </span>
        </div>
        <label className="label label-soft-secondary" style={{ margin: 0, marginLeft: "auto" }}>
          <RenderCustomFieldFilterType type={filter.type} /> {filter.type === "flat" ? "Simple" : "Complex"} filter
        </label>
      </div>
      <div className="flex row-nowrap align-center" style={{ marginLeft: "auto" }}>
        <button
          className="btn btn-xs btn-ghost-secondary"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onEditCallback(filter);
          }}
          title="Edit"
        >
          <LucideIcon name="pencil" />
        </button>
        <button
          className="btn btn-xs btn-ghost-danger"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDeleteCallback(id);
          }}
          title="Delete"
        >
          <LucideIcon name="trash-2" />
        </button>
      </div>
    </div>
  );
};
